/**
 * Adeline Theme - Global JavaScript
 * Handles mobile navigation, FAQ accordions, and other global interactions
 */

(function() {
  'use strict';

  // ============================================
  // Mobile Navigation Drawer
  // ============================================
  const mobileToggle = document.querySelector('[data-mobile-toggle]');
  const mobileClose = document.querySelector('[data-mobile-close]');
  const mobileDrawer = document.getElementById('mobile-drawer');
  const drawerOverlay = document.querySelector('[data-drawer-overlay]');
  let focusableElements = [];
  let firstFocusable = null;
  let lastFocusable = null;

  function getFocusableElements(container) {
    return Array.from(container.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
  }

  function trapFocus(e) {
    if (!mobileDrawer || !mobileDrawer.classList.contains('is-open')) return;
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  function openMobileDrawer() {
    if (!mobileDrawer || !drawerOverlay) return;
    mobileDrawer.classList.add('is-open');
    drawerOverlay.classList.add('is-visible');
    document.documentElement.style.overflow = 'hidden';
    if (mobileToggle) mobileToggle.setAttribute('aria-expanded', 'true');

    // Set aria-hidden on main content
    document.querySelectorAll('main, header:not(.mobile-drawer), footer').forEach((el) => {
      el.setAttribute('aria-hidden', 'true');
    });
    mobileDrawer.removeAttribute('aria-hidden');

    // Setup focus trap
    focusableElements = getFocusableElements(mobileDrawer);
    if (focusableElements.length > 0) {
      firstFocusable = focusableElements[0];
      lastFocusable = focusableElements[focusableElements.length - 1];
      firstFocusable.focus();
    }

    document.addEventListener('keydown', trapFocus);
  }

  function closeMobileDrawer() {
    if (!mobileDrawer || !drawerOverlay) return;
    mobileDrawer.classList.remove('is-open');
    drawerOverlay.classList.remove('is-visible');
    document.documentElement.style.overflow = '';

    // Remove aria-hidden from main content
    document.querySelectorAll('main, header:not(.mobile-drawer), footer').forEach((el) => {
      el.removeAttribute('aria-hidden');
    });
    mobileDrawer.setAttribute('aria-hidden', 'true');

    document.removeEventListener('keydown', trapFocus);

    if (mobileToggle) {
      mobileToggle.setAttribute('aria-expanded', 'false');
      mobileToggle.focus();
    }
  }

  if (mobileToggle) {
    mobileToggle.addEventListener('click', openMobileDrawer);
  }

  if (mobileClose) {
    mobileClose.addEventListener('click', closeMobileDrawer);
  }

  if (drawerOverlay) {
    drawerOverlay.addEventListener('click', closeMobileDrawer);
  }

  // Close on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && mobileDrawer && mobileDrawer.classList.contains('is-open')) {
      closeMobileDrawer();
    }
  });

  // ============================================
  // Header Search Toggle
  // ============================================
  const headerSearchToggles = document.querySelectorAll('[data-header-search-toggle]');
  const headerSearchPanel = document.getElementById('header-search-panel');

  if (headerSearchToggles.length && headerSearchPanel) {
    const headerSearchInput = headerSearchPanel.querySelector('[data-header-search-input]');
    const headerSearchForm = headerSearchPanel.querySelector('[data-header-search-form]');
    const quickSearchGroup = headerSearchPanel.querySelector('[data-header-search-quick]');
    const liveSearchGroup = headerSearchPanel.querySelector('[data-header-search-live]');
    const liveSearchStatus = headerSearchPanel.querySelector('[data-header-search-status]');
    const liveSearchResults = headerSearchPanel.querySelector('[data-header-search-results]');
    const desktopHeaderSearchToggle = document.querySelector('.hdr-acts [data-header-search-toggle]');
    const predictiveSearchRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    const searchCurrency = headerSearchForm && headerSearchForm.dataset.currency
      ? headerSearchForm.dataset.currency
      : ((window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP');
    const searchMoneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency: searchCurrency,
      currencyDisplay: 'narrowSymbol'
    });
    let predictiveSearchTimer = null;
    let predictiveSearchController = null;
    let predictiveSearchRequestId = 0;

    function formatSearchMoney(value) {
      const amount = Number(value);

      if (!Number.isFinite(amount)) {
        return '';
      }

      return searchMoneyFormatter.format(amount);
    }

    function getPredictiveProductPricing(item) {
      if (!item || typeof item !== 'object') {
        return {
          current: '',
          compareAt: '',
          onSale: false
        };
      }

      const priceValue = item.price_min ?? item.price;
      const compareAtValue = item.compare_at_price ?? item.compare_at_price_min ?? item.compare_at_price_max;
      const currentAmount = Number(priceValue);
      const compareAtAmount = Number(compareAtValue);
      const current = formatSearchMoney(priceValue);
      const compareAt = formatSearchMoney(compareAtValue);
      const onSale = Number.isFinite(currentAmount)
        && Number.isFinite(compareAtAmount)
        && compareAtAmount > currentAmount;

      return {
        current: current,
        compareAt: onSale ? compareAt : '',
        onSale: onSale
      };
    }

    function getPredictiveProductImage(item) {
      if (!item || typeof item !== 'object') return '';

      if (item.featured_image) {
        if (typeof item.featured_image === 'string') return item.featured_image;
        if (item.featured_image.url) return item.featured_image.url;
      }

      if (item.image) {
        if (typeof item.image === 'string') return item.image;
        if (item.image.url) return item.image.url;
      }

      if (item.featured_media && item.featured_media.preview_image && item.featured_media.preview_image.url) {
        return item.featured_media.preview_image.url;
      }

      return '';
    }

    function syncHeaderSearchPanelPosition() {
      if (window.innerWidth < 769) {
        headerSearchPanel.style.removeProperty('left');
        headerSearchPanel.style.removeProperty('top');
        headerSearchPanel.style.removeProperty('width');
        return;
      }

      if (!desktopHeaderSearchToggle) return;

      const wrap = headerSearchPanel.closest('.wrap');
      if (!wrap) return;

      const wrapRect = wrap.getBoundingClientRect();
      const toggleRect = desktopHeaderSearchToggle.getBoundingClientRect();
      const panelWidth = Math.min(420, Math.max(280, wrapRect.width - 24));
      const maxLeft = Math.max(0, wrapRect.width - panelWidth);
      const alignedLeft = toggleRect.right - wrapRect.left - panelWidth;
      const panelLeft = Math.min(Math.max(alignedLeft, 0), maxLeft);
      const panelTop = toggleRect.bottom - wrapRect.top + 10;

      headerSearchPanel.style.left = panelLeft + 'px';
      headerSearchPanel.style.top = panelTop + 'px';
      headerSearchPanel.style.width = panelWidth + 'px';
    }

    function clearPredictiveSearchResults() {
      if (!liveSearchResults || !liveSearchStatus || !liveSearchGroup) return;

      liveSearchResults.innerHTML = '';
      liveSearchStatus.textContent = 'Start typing to see suggestions.';
      liveSearchGroup.hidden = true;
      if (headerSearchInput) {
        headerSearchInput.setAttribute('aria-expanded', 'false');
      }
    }

    function setPredictiveSearchLoading() {
      if (!liveSearchStatus || !liveSearchResults || !liveSearchGroup) return;

      liveSearchGroup.hidden = false;
      liveSearchResults.innerHTML = '';
      liveSearchStatus.textContent = 'Searching...';
      if (quickSearchGroup) {
        quickSearchGroup.hidden = true;
      }
      if (headerSearchInput) {
        headerSearchInput.setAttribute('aria-expanded', 'true');
      }
    }

    function renderPredictiveSearchResults(term, payload) {
      if (!liveSearchResults || !liveSearchStatus || !liveSearchGroup) return;

      const resourceResults = payload && payload.resources && payload.resources.results
        ? payload.resources.results
        : {};
      const items = [];

      [
        ['products', 'Product'],
        ['collections', 'Collection']
      ].forEach(([key, label]) => {
        const resourceItems = Array.isArray(resourceResults[key]) ? resourceResults[key] : [];

        resourceItems.forEach((item) => {
          if (items.length >= 6) return;

          const title = item.title || item.text || item.styled_text || term;
          const url = item.url || ('/search?q=' + encodeURIComponent(title));

          if (!title || !url) return;

          items.push({
            title: title,
            type: label,
            url: url,
            image: key === 'products' ? getPredictiveProductImage(item) : '',
            pricing: key === 'products' ? getPredictiveProductPricing(item) : null,
            isProduct: key === 'products'
          });
        });
      });

      liveSearchGroup.hidden = false;
      liveSearchResults.innerHTML = '';

      if (!items.length) {
        liveSearchStatus.textContent = 'No matching suggestions found.';
      } else {
        liveSearchStatus.textContent = 'Top matches';

        items.forEach((item) => {
          const link = document.createElement('a');
          const title = document.createElement('span');
          const meta = document.createElement('span');
          const content = document.createElement('span');

          link.className = 'hdr-search-suggestion';
          link.href = item.url;

          content.className = 'hdr-search-suggestion__content';

          title.className = 'hdr-search-suggestion__title';
          title.textContent = item.title;

          meta.className = 'hdr-search-suggestion__meta';
          meta.textContent = item.type;

          if (item.isProduct && item.image) {
            const image = document.createElement('img');

            image.className = 'hdr-search-suggestion__image';
            image.src = item.image;
            image.alt = item.title;
            image.loading = 'lazy';
            image.width = 56;
            image.height = 56;
            link.appendChild(image);
          }

          if (item.isProduct) {
            const pricing = item.pricing || {};
            const currentPrice = pricing.current || '';
            const compareAtPrice = pricing.compareAt || '';

            content.appendChild(title);

            if (currentPrice) {
              const price = document.createElement('span');

              price.className = 'hdr-search-suggestion__price';

              if (compareAtPrice) {
                const current = document.createElement('span');
                const compareAt = document.createElement('span');

                current.className = 'hdr-search-suggestion__price-current';
                current.textContent = currentPrice;

                compareAt.className = 'hdr-search-suggestion__price-compare';
                compareAt.textContent = compareAtPrice;

                price.appendChild(current);
                price.appendChild(compareAt);
              } else {
                price.textContent = currentPrice;
              }

              content.appendChild(price);
            }
          } else {
            content.appendChild(title);
          }

          link.appendChild(content);
          link.appendChild(meta);
          liveSearchResults.appendChild(link);
        });
      }

      if (headerSearchForm && term) {
        const searchAllLink = document.createElement('a');
        const searchAllLabel = document.createElement('span');
        const searchAllMeta = document.createElement('span');

        searchAllLink.className = 'hdr-search-suggestion';
        searchAllLink.href = headerSearchForm.action + '?q=' + encodeURIComponent(term);

        searchAllLabel.className = 'hdr-search-suggestion__title';
        searchAllLabel.textContent = 'Search for "' + term + '"';

        searchAllMeta.className = 'hdr-search-suggestion__meta';
        searchAllMeta.textContent = 'View all';

        searchAllLink.appendChild(searchAllLabel);
        searchAllLink.appendChild(searchAllMeta);
        liveSearchResults.appendChild(searchAllLink);
      }

      if (quickSearchGroup) {
        quickSearchGroup.hidden = true;
      }
      if (headerSearchInput) {
        headerSearchInput.setAttribute('aria-expanded', 'true');
      }
    }

    function fetchPredictiveSearchResults(term) {
      if (!term) {
        if (quickSearchGroup) {
          quickSearchGroup.hidden = false;
        }
        clearPredictiveSearchResults();
        return;
      }

      if (predictiveSearchController) {
        predictiveSearchController.abort();
      }

      predictiveSearchController = new AbortController();
      predictiveSearchRequestId += 1;
      const requestId = predictiveSearchRequestId;
      const rootPath = predictiveSearchRoot.slice(-1) === '/' ? predictiveSearchRoot : predictiveSearchRoot + '/';
      const params = new URLSearchParams();

      params.set('q', term);
      params.set('resources[type]', 'product,collection');
      params.set('resources[limit]', '6');
      params.set('resources[limit_scope]', 'each');
      params.set('resources[options][unavailable_products]', 'hide');

      setPredictiveSearchLoading();

      fetch(rootPath + 'search/suggest.json?' + params.toString(), {
        signal: predictiveSearchController.signal,
        headers: {
          Accept: 'application/json'
        }
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Predictive search request failed');
          }

          return response.json();
        })
        .then((payload) => {
          if (requestId !== predictiveSearchRequestId) return;
          renderPredictiveSearchResults(term, payload);
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;

          if (!liveSearchStatus || !liveSearchGroup || !liveSearchResults) return;

          liveSearchGroup.hidden = false;
          liveSearchResults.innerHTML = '';
          liveSearchStatus.textContent = 'Suggestions are unavailable right now.';
        });
    }

    function queuePredictiveSearch(term) {
      window.clearTimeout(predictiveSearchTimer);

      if (!term) {
        if (quickSearchGroup) {
          quickSearchGroup.hidden = false;
        }
        clearPredictiveSearchResults();
        return;
      }

      predictiveSearchTimer = window.setTimeout(() => {
        fetchPredictiveSearchResults(term);
      }, 220);
    }

    function setHeaderSearchState(isOpen) {
      headerSearchPanel.hidden = !isOpen;
      headerSearchToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });

      if (isOpen && headerSearchInput) {
        syncHeaderSearchPanelPosition();
        if (!headerSearchInput.value.trim() && quickSearchGroup) {
          quickSearchGroup.hidden = false;
        } else if (headerSearchInput.value.trim()) {
          queuePredictiveSearch(headerSearchInput.value.trim());
        }
        setTimeout(() => headerSearchInput.focus(), 100);
      } else if (!isOpen) {
        window.clearTimeout(predictiveSearchTimer);
        if (predictiveSearchController) {
          predictiveSearchController.abort();
        }
        if (!headerSearchInput || !headerSearchInput.value.trim()) {
          clearPredictiveSearchResults();
          if (quickSearchGroup) {
            quickSearchGroup.hidden = false;
          }
        }
      }
    }

    headerSearchToggles.forEach((toggle) => {
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        setHeaderSearchState(headerSearchPanel.hidden);
      });
    });

    document.addEventListener('click', (event) => {
      if (headerSearchPanel.hidden) return;
      if (headerSearchPanel.contains(event.target)) return;
      if ([...headerSearchToggles].some((toggle) => toggle.contains(event.target))) return;
      setHeaderSearchState(false);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !headerSearchPanel.hidden) {
        setHeaderSearchState(false);
      }
    });

    if (headerSearchInput) {
      headerSearchInput.addEventListener('input', () => {
        queuePredictiveSearch(headerSearchInput.value.trim());
      });
    }

    window.addEventListener('resize', () => {
      if (!headerSearchPanel.hidden) {
        syncHeaderSearchPanelPosition();
      }
    });
  }

  // ============================================
  // FAQ Accordion
  // ============================================
  const faqTriggers = document.querySelectorAll('[data-faq-trigger]');

  faqTriggers.forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      const panelId = trigger.getAttribute('aria-controls');
      const panel = document.getElementById(panelId);

      if (!panel) return;

      // Close all other FAQ items in the same list
      const faqList = trigger.closest('[data-faq-list]');
      if (faqList) {
        faqList.querySelectorAll('[data-faq-trigger]').forEach((otherTrigger) => {
          if (otherTrigger !== trigger) {
            otherTrigger.setAttribute('aria-expanded', 'false');
            const otherPanelId = otherTrigger.getAttribute('aria-controls');
            const otherPanel = document.getElementById(otherPanelId);
            if (otherPanel) otherPanel.hidden = true;
          }
        });
      }

      // Toggle current item
      trigger.setAttribute('aria-expanded', !isExpanded);
      panel.hidden = isExpanded;

      // Update icon
      const icon = trigger.querySelector('.faq-icon .mi');
      if (icon) {
        icon.textContent = isExpanded ? 'add' : 'remove';
      }
    });
  });

  // ============================================
  // Smooth scroll for anchor links
  // ============================================
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener('click', (e) => {
      const targetId = anchor.getAttribute('href');
      if (targetId === '#') return;

      const target = document.querySelector(targetId);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
        // Update focus for accessibility
        target.setAttribute('tabindex', '-1');
        target.focus();
      }
    });
  });

  // ============================================
  // Chat section interactions
  // Only runs on non-PDP pages - product-page.js handles PDP chat
  // ============================================
  if (!document.querySelector('[data-product-section]')) {
    const chatInput = document.getElementById('chat-input');
    const chatSend = document.getElementById('chat-send');
    const chatMsgs = document.getElementById('chat-msgs');
    const suggestBtns = document.querySelectorAll('[data-suggest]');
    const chatSection = document.querySelector('.chat-section[data-chat-webhook]');
    const WEBHOOK_URL = chatSection ? chatSection.dataset.chatWebhook : '';

    function getSessionId() {
      let sessionId = sessionStorage.getItem('chat_session_id');
      if (!sessionId) {
        sessionId = 'session_' + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('chat_session_id', sessionId);
      }
      return sessionId;
    }

    function addChatMessage(text, isUser = true) {
      if (!chatMsgs) return;
      const msgDiv = document.createElement('div');
      msgDiv.className = `chat-msg ${isUser ? 'chat-msg-user' : 'chat-msg-bot'}`;
      msgDiv.textContent = text;
      chatMsgs.appendChild(msgDiv);
      chatMsgs.scrollTop = chatMsgs.scrollHeight;
    }

    function addTypingIndicator() {
      if (!chatMsgs) return null;
      const typing = document.createElement('div');
      typing.className = 'chat-msg chat-msg-bot chat-msg-typing';
      typing.setAttribute('aria-label', 'Sleep expert is typing');
      typing.innerHTML = '<span class="chat-dot"></span><span class="chat-dot"></span><span class="chat-dot"></span>';
      chatMsgs.appendChild(typing);
      chatMsgs.scrollTop = chatMsgs.scrollHeight;
      return typing;
    }

    function extractChatReply(payload) {
      if (!payload) return '';

      if (typeof payload === 'string') {
        return payload.trim();
      }

      if (Array.isArray(payload)) {
        for (const item of payload) {
          const nested = extractChatReply(item);
          if (nested) return nested;
        }
        return '';
      }

      if (typeof payload === 'object') {
        const preferredKeys = ['reply', 'message', 'text', 'output', 'response', 'answer', 'content'];
        for (const key of preferredKeys) {
          if (key in payload) {
            const nested = extractChatReply(payload[key]);
            if (nested) return nested;
          }
        }

        for (const value of Object.values(payload)) {
          const nested = extractChatReply(value);
          if (nested) return nested;
        }
      }

      return '';
    }

    async function sendMessage(text) {
      const trimmed = String(text || '').trim();
      if (!trimmed) return;

      addChatMessage(trimmed, true);
      if (chatInput) chatInput.value = '';

      const typing = addTypingIndicator();

      if (!WEBHOOK_URL) {
        // Fallback to placeholder if no webhook configured
        setTimeout(() => {
          if (typing) typing.remove();
          addChatMessage('Thanks for your message! Our team typically responds within 2 minutes during business hours.', false);
        }, 1000);
        return;
      }

      try {
        const response = await fetch(WEBHOOK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: trimmed,
            timestamp: new Date().toISOString(),
            sessionId: getSessionId(),
          }),
        });

        if (typing) typing.remove();

        if (response.ok) {
          const responseText = await response.text();
          let reply = '';

          if (responseText) {
            try {
              const data = JSON.parse(responseText);
              reply = extractChatReply(data);
            } catch {
              reply = responseText.trim();
            }
          }

          if (!reply) {
            addChatMessage('No reply came back from the assistant. Please try again.', false);
            return;
          }

          addChatMessage(reply, false);
        } else {
          throw new Error('Network error');
        }
      } catch (error) {
        if (typing) typing.remove();
        addChatMessage('Sorry, there was an error connecting to our sleep experts. Please try again or call us directly.', false);
        console.error('Chat webhook error:', error);
      }
    }

    if (chatSend && chatInput) {
      chatSend.addEventListener('click', () => {
        sendMessage(chatInput.value);
      });

      chatInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          sendMessage(chatInput.value);
        }
      });
    }

    suggestBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const suggestion = btn.getAttribute('data-suggest');
        if (suggestion) {
          sendMessage(suggestion);
        }
      });
    });
  }

  // ============================================
  // Review search filter
  // ============================================
  // Query by name attribute to handle section-specific IDs (review-search-{{ section.id }})
  const reviewSearchInput = document.querySelector('[name="review-search"]');
  if (reviewSearchInput) {
    reviewSearchInput.addEventListener('input', (e) => {
      const query = e.target.value.toLowerCase().trim();
      const reviewCards = document.querySelectorAll('.rev-card');

      reviewCards.forEach((card) => {
        const text = card.textContent.toLowerCase();
        card.style.display = text.includes(query) ? '' : 'none';
      });
    });
  }

})();
